import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { GoogleMap, Marker, OverlayView } from '@react-google-maps/api';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Paper, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';

const containerStyle = {
  width: '100%',
  height: '100%',
};

// メモ吹き出しコンポーネント
function MemoBalloon({ memo, onClick }) {
  return (
    <OverlayView
      position={{ lat: memo.lat, lng: memo.lon }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick && onClick(memo);
        }}
        style={{
          transform: "translate(-50%, -100%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer"
        }}
      >
        {/* バルーン */}
        <div style={{
          background: "#fff",
          padding: "4px 8px",
          borderRadius: 6,
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          fontSize: 12,
          maxWidth: 200,
          width: "max-content",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        }}>
          <Typography variant="inherit" sx={{ fontWeight: 'bold' }}>{memo.place}</Typography>
          <Typography variant="inherit" sx={{ whiteSpace: 'pre-line' }}>{memo.contents}</Typography>
        </div>
        {/* 三角 */}
        <div style={{
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: "6px solid white",
          marginTop: "-1px"
        }}/>
        {/* 丸アイコン */}
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: '#ffbb00',
          border: "2px solid white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.4)"
        }}>
          <StickyNote2Icon style={{ color: "white", fontSize: 22 }} />
        </div>
      </div>
    </OverlayView>
  );
}

// メモ編集用のGoogleMapコンポーネント
export default function MemoEditMap({
  apiKey,
  memos = [],
  defaultCenter = { lat: 36.0, lng: 138.0 },
  onNewMarkerClick, 
  onEditMarkerClick, 
  countryRestriction = 'JP',
}) {
  const [center, setCenter] = useState(defaultCenter);
  const [draftMarker, setDraftMarker] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [activeIndex,setActiveIndex] = useState(-1);
  const mapRef = useRef(null);
  const autocompleteService = useRef(null);
  const sessionToken = useRef(null);
  const placesService = useRef(null);
  const debounceRef = useRef(null);
  const isFirstLoad = useRef(true);
  const location = useLocation();

  // コンポーネントアンマウント時にタイマーをクリア
  useEffect(()=>{
    return ()=>{
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    }
  },[])

  // ページ遷移時
  useEffect(() => {
    // ページ遷移時は初回にする
    isFirstLoad.current = true;
  }, [location.key]);

  // メモが変更された
  useEffect(() => {
    // 初回ロード時のみ
    if (isFirstLoad.current && memos.length > 0 && mapRef.current) {
      // マーカーに合わせてズームとセンターを調整
      fitMapToMemos();
      // 初回ロード完了
      isFirstLoad.current = false;
    }
    // 新規マーカー削除
    setDraftMarker(null);
  }, [memos]);

  // マーカーに合わせてズームとセンターを調整する関数（初回以外も呼べるように）
  const fitMapToMemos = () => {
    // 地図APIロード前はやらない
    if (!mapRef.current || !window.google) return;
    // マーカーがない場合はやらない
    if (memos.length === 0) return;
    // マーカーが1件だけの場合はその位置にズーム
    if (memos.length === 1) {
      mapRef.current.setCenter({ lat: memos[0].lat, lng: memos[0].lon });
      mapRef.current.setZoom(14);
    } else {
      // 複数マーカーがある場合は全てのマーカーが見えるようにズームとセンターを調整
      const bounds = new window.google.maps.LatLngBounds();
      memos.forEach(m => bounds.extend({ lat: m.lat, lng: m.lon }));
      mapRef.current.fitBounds(bounds, 50); // 50pxの余白をつける
    }
  }

  // 地図ロード
  const handleMapLoad = (map)=>{
    mapRef.current = map

    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!sessionToken.current) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    if (!placesService.current) {
      placesService.current = new window.google.maps.places.PlacesService(map);
    }
    // マーカーに合わせてズームとセンターを調整
    fitMapToMemos();
  }

  // 地図クリック
  const handleMapClick = e => {
    // クリック位置の緯度経度を取得
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    // 新規マーカー更新
    setDraftMarker({ lat: lat, lon: lng, placeId: e.placeId || null, place: null, address: null, contents: null });
  };

  // 入力に応じてサジェスト取得
  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    // 前のタイマーをキャンセル
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // サジェスト取得タイミングを0.5秒遅らせる（debounce）
    debounceRef.current = setTimeout(() => {
      // サービスとトークンと入力がない場合はサジェストクリア
      if (!autocompleteService.current || !sessionToken.current || !value) {
        setSuggestions([]);
        return;
      }
      // サジェスト取得
      autocompleteService.current.getPlacePredictions(
        { input: value, componentRestrictions: countryRestriction ? { country: countryRestriction } : {}, sessionToken: sessionToken.current },
        preds => {
          setSuggestions(Array.isArray(preds) ? preds.slice(0, 10) : []);
        }
      );
    }, 500);
  };

  // サジェスト選択: markerとcenter両方変更
  const handleSelectSuggestion = async pred => {
    //setInputValue(pred.description);
    setInputValue(pred.structured_formatting.main_text);
    setSuggestions([]);
    setActiveIndex(-1)

    // 位置情報取得
    if (!placesService.current) return;
    placesService.current.getDetails(
      { placeId: pred.place_id, fields: ['geometry', 'name', 'formatted_address'], sessionToken: sessionToken.current }, 
      place => {
        if (!place || !place.geometry) return;
        const pos = { 
          lat: place.geometry.location.lat(), 
          lng: place.geometry.location.lng() 
        };
        // 新規マーカー更新
        setDraftMarker({ 
          lat: pos.lat, 
          lon: pos.lng, 
          placeId: pred.place_id, 
          place: pred.structured_formatting.main_text, 
          address: place.formatted_address,
          contents: ""
        });
        // 地図を選択した場所に移動してズーム
        mapRef.current.panTo(pos);
        mapRef.current.setZoom(16);
        // 次の検索のために token を更新
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();       
    });
  };

  // 検索バー入力クリアボタン
  const handleInputClear = () => {
    setInputValue('');
    setSuggestions([]);
    setActiveIndex(-1)
    // 入力のみクリアで新規マーカーは削除しない
    //setDraftMarker(null);
  };

  // サジェストリストのキーボード操作: 上下で選択、Enterで決定
  const handleSuggestKeyDown = e => {
    if(!suggestions.length) return;

    if(e.key==="ArrowDown"){
      e.preventDefault()
      setActiveIndex(prev=>prev<suggestions.length-1?prev+1:prev)
    }
    if(e.key==="ArrowUp"){
      e.preventDefault()
      setActiveIndex(prev=>prev>0?prev-1:0)
    }
    if(e.key==="Enter"){
      if(activeIndex>=0){
        e.preventDefault()
        handleSelectSuggestion(suggestions[activeIndex])
      }
    }
    if(e.key==="Escape"){
      e.preventDefault()
      e.stopPropagation()
      setSuggestions([])
      setActiveIndex(-1)
    }
  };

  // 地図と検索バーを重ねて表示
  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* MUI検索バー */}
      <div style={{ position: 'absolute', zIndex: 10, left: 220, top: 10, minWidth: 400 }}>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleSuggestKeyDown}
          placeholder="場所を検索..."
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              inputValue && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleInputClear}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            ),
            style: { backgroundColor: '#fff', fontSize: 14 },
          }}
        />
        {/* サジェストリスト */}
        {suggestions.length > 0 && placesService.current && (
          <Paper
            sx={{
            mt:0.5,
            maxHeight:320,
            overflow:"auto"
            }}
          >
            <List dense>
              {suggestions.map((s,index)=>(
              <ListItemButton
                key={s.place_id}
                selected={index===activeIndex}
                onMouseEnter={()=>setActiveIndex(index)}
                onClick={()=>handleSelectSuggestion(s)}
              >
                <ListItemText
                  primary={s.structured_formatting.main_text}
                  secondary={s.structured_formatting.secondary_text}
                  primaryTypographyProps={{fontSize:14}}
                  secondaryTypographyProps={{fontSize:12}}
                />
              </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </div>
      {/* 地図 */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={15}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        options={{
          clickableIcons: true,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          draggableCursor: 'default', // 通常時は矢印
          draggingCursor: 'grab',     // ドラッグ中は移動カーソル
        }}
      >
        {/* 既存メモのマーカー */}
        {memos.map((memo) => (
          <MemoBalloon
            key={memo.id}
            memo={memo}
            onClick={onEditMarkerClick}
          />
        ))}
        {/* 新規マーカー */}
        {draftMarker && (
          <Marker
            position={{ lat: draftMarker.lat, lng: draftMarker.lon }}
            onClick={(e) => {
              e.domEvent.stopPropagation();
              onNewMarkerClick && onNewMarkerClick(draftMarker);
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
}